import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import Button from "@/components/Button";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import Progress from "@/components/Progress";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import useAmplitude from "@/hooks/useAmplitude";
import useModal from "@/hooks/useModal";
import { SECTION_ANALYTICS_EVENTS, getSectionAnalyticsData } from "@/pages/sections/_utils/amplitude";

import { useChangeRequestReassignEmailPreview } from "../hooks/useChangeRequestReassignEmailPreview";
import { CHANGE_REQUESTS_QUERY, Node, REASSIGN_CHANGE_REQUEST_MUTATION, REASSIGN_SECTIONS_QUERY } from "../queries";

type Props = {
  changeRequest: Node;
  onClosed(): void;
};

export default function ReassignModal({ changeRequest, onClosed }: Props) {
  const { Modal, save, close } = useModal();
  const { trackEvent } = useAmplitude();
  const [targetSectionId, setTargetSectionId] = useState("");
  const [message, setMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useChangeRequestReassignEmailPreview(changeRequest.id);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const sourceSectionId = changeRequest.section.id;
  const { data, loading: sectionsLoading } = useQuery(REASSIGN_SECTIONS_QUERY);
  const [reassign, { loading: reassigning }] = useMutation(REASSIGN_CHANGE_REQUEST_MUTATION, {
    refetchQueries: [CHANGE_REQUESTS_QUERY],
    awaitRefetchQueries: true
  });

  const options = useMemo(() => {
    const sections = data?.sections ?? [];
    return sections
      .filter((section) => section.id !== sourceSectionId)
      .map((section) => ({ value: section.id, displayText: section.name }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [data, sourceSectionId]);

  const onSave = () =>
    save(
      async () => {
        const result = await reassign({
          variables: {
            id: changeRequest.id,
            targetSectionId,
            message: message.trim() || undefined
          }
        });
        if (result.data?.reassignChangeRequest?.success) {
          trackEvent(
            SECTION_ANALYTICS_EVENTS.changeRequestReassigned,
            getSectionAnalyticsData({
              source: "section_change_requests",
              sectionId: sourceSectionId,
              targetSectionId,
              changeRequestId: changeRequest.id,
              changeType: changeRequest.change.__typename,
              action: "reassign",
              hasMessage: Boolean(message.trim())
            })
          );
        }
        return result;
      },
      (data) => data.reassignChangeRequest,
      "Change request reassigned"
    );

  const handlePreviewEmail = () => {
    if (!targetSectionId) {
      return;
    }

    setPreviewOpen(true);
    void loadPreview(targetSectionId);
  };

  return (
    <>
      <Modal
        displayOverflow
        onClosed={onClosed}
        headline="Reassign Change Request"
        className={EMAIL_PREVIEW_MODAL_CLASSNAME}
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={!targetSectionId || reassigning}>
            <Button variant="text" onClick={close} disabled={reassigning}>
              Cancel
            </Button>
            <Button disabled={!targetSectionId || reassigning} onClick={onSave} icon="swap_horiz">
              Reassign
            </Button>
          </EmailPreviewModalActions>
        }
      >
        {sectionsLoading ? (
          <Progress variant="linear" />
        ) : (
          <div className="flex flex-col gap-6">
            <p className="max-w-sm">
              Move this change request to a different section's queue. The new section will be notified.
            </p>

            <Select
              label="New Section"
              className="w-full"
              options={options}
              value={targetSectionId}
              onSelect={setTargetSectionId}
              placeholder="Select a section"
              data-testid="reassign-section-select"
            />

            <TextField
              className="w-full h-28"
              variant="outlined"
              type="textarea"
              label="Reason (Optional)"
              value={message}
              onChangeText={setMessage}
              data-testid="reassign-reason"
            />
          </div>
        )}
      </Modal>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
